(() => {
  'use strict';

  if (window.__tkCartPageInitialized) return;
  window.__tkCartPageInitialized = true;

  const rootPath = window.Shopify?.routes?.root || '/';
  let isRemoving = false;

  function isCartPage() {
    return document.body.classList.contains('template-cart');
  }

  function getCartUrl(endpoint) {
    return `${rootPath}${endpoint}`;
  }

  async function fetchCart() {
    const response = await fetch(getCartUrl('cart.js'), {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    const cart = await response.json();
    if (!response.ok) {
      throw new Error(cart.description || cart.message || 'Unable to load the Shopify cart.');
    }
    return cart;
  }

  async function removeBundle(bundleId) {
    const cart = await fetchCart();
    const matchingItems = cart.items.filter(item =>
      String(item.properties?._bundle_id || '') === String(bundleId)
    );
    if (!matchingItems.length) {
      throw new Error('No cart items matched this bundle ID.');
    }
    const updates = {};
    matchingItems.forEach(item => {
      if (item.key) updates[item.key] = 0;
    });
    if (!Object.keys(updates).length) {
      throw new Error('Shopify did not return valid line-item keys.');
    }
    const response = await fetch(getCartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Shopify could not remove the bundle.');
    }
    return result;
  }

  // ─── NEW: Remove single item from a bundle (+ warranty, + cleanup) ───
  async function removeBundleItem(lineKey) {
    const cart = await fetchCart();
    const targetItem = cart.items.find(ci => ci.key === lineKey);

    if (!targetItem) {
      throw new Error('Cart item not found. Refresh and try again.');
    }

    const bundleId = String(targetItem.properties?._bundle_id || '');
    if (!bundleId) {
      // Not a bundle item, normal removal
      const response = await fetch(getCartUrl('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to remove item.');
      return resultCt;
    }

    const bundleItems = cart.items.filter(ci =>
      String(ci.properties?._bundle_id || '') === bundleId
    );

    const toRemove = new Set([targetItem.key]);

    // Find associated warranty
    const applianceSku = targetItem.sku || '';
    const applianceTitle = targetItem.product_title || targetItem.title || '';
    const associatedWarranty = bundleItems.find(ci => {
      if (ci.key === targetItem.key) return false;
      const covered = ci.properties?.['Covered appliance'] || '';
      const skuProp = ci.properties?.['Appliance SKU'] || '';
      return (applianceSku && skuProp === applianceSku) ||
             (applianceTitle && covered === applianceTitle);
    });
    if (associatedWarranty) toRemove.add(associatedWarranty.key);

    // Check remaining appliances
    const remainingAppliances = bundleItems.filter(ci => {
      if (toRemove.has(ci.key)) return false;
      return !(ci.properties?.['Covered appliance']);
    });

    // If <2 appliances remain, remove all remaining bundle items
    if (remainingAppliances.length < 2) {
      bundleItems.forEach(ci => toRemove.add(ci.key));
    }

    const updates = {};
    toRemove.forEach(key => { updates[key] = 0; });

    const response = await fetch(getCartUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Could not remove the bundle item.');
    }
    return result;
  }

  async function removeRegularItem(lineKey) {
    const cart = await fetchCart();
    const item = cart.items.find(ci => ci.key === lineKey);
    if (!item) throw new Error('The cart item was not found.');
    if (item.properties?._bundle_id) {
      throw new Error('This item belongs to a bundle. Use the item Remove button.');
    }
    const response = await fetch(getCartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: item.key, quantity: 0 })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Unable to remove this item.');
    return result;
  }

  function initBundleCartPage() {
    if (!isCartPage()) return;

    const tableBody =
      document.querySelector('.order-summary__body') ||
      document.querySelector('.cart__items') ||
      document.querySelector('[data-cart-items]');

    if (!tableBody || tableBody.querySelector('.bundle-header-row')) return;

    const rows = [...tableBody.querySelectorAll(':scope > tr')];
    const isTableLayout = rows.length > 0;
    const itemRows = isTableLayout
      ? rows
      : [...tableBody.querySelectorAll(':scope > .cart-item, :scope > div')];

    if (!itemRows.length) return;

    const bundles = new Map();

    itemRows.forEach(row => {
      const item = row.querySelector('line-item[data-bundle-id]') || row;
      const bundleId = (item.dataset?.bundleId || '').trim();
      if (!bundleId) return;
      if (!bundles.has(bundleId)) bundles.set(bundleId, []);
      bundles.get(bundleId).push({ row, item });
    });

    bundles.forEach((entries, bundleId) => {
      if (entries.length < 2) return;

      const first = entries[0];

      // Header
      const headerRow = document.createElement(isTableLayout ? 'tr' : 'div');
      headerRow.className = 'bundle-header-row';
      const headerCell = document.createElement(isTableLayout ? 'td' : 'div');
      if (isTableLayout) headerCell.colSpan = 3;
      const header = document.createElement('div');
      header.className = 'cart-bundle-header';
      const title = document.createElement('h3');
      title.textContent = first.item.dataset.bundleName || 'Thor Kitchen Bundle';
      const subtitle = document.createElement('p');
      subtitle.textContent = `${entries.length} Items Included`;
      header.append(title, subtitle);
      headerCell.appendChild(header);
      headerRow.appendChild(headerCell);
      first.row.before(headerRow);

      // Bundle item rows — add individual Remove button
      entries.forEach(({ row, item }) => {
        row.classList.add('bundle-product-row');

        // Hide quantity selector
        row.querySelectorAll('.quantity-selector').forEach(el => el.remove());

        // ─── FIX: Add individual Remove button to each bundle item ───
        const infoEl = item.querySelector('.A') || row.querySelector('.line-item-info');
        if (infoEl && !infoEl.querySelector('.bundle-item-remove')) {
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'bundle-item-remove';
          removeBtn.dataset.lineKey = item.dataset?.lineKey || '';
          removeBtn.textContent = 'Remove';
          infoEl.appendChild(removeBtn);
        }
      });

      // Footer
      const footerRow = document.createElement(isTableLayout ? 'tr' : 'div');
      footerRow.className = 'bundle-footer-row';
      const footerCell = document.createElement(isTableLayout ? 'td' : 'div');
      if (isTableLayout) footerCell.colSpan = 3;
      const footer = document.createElement('div');
      footer.className = 'cart-bundle-footer';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-remove-bundle';
      button.dataset.bundleId = bundleId;
      button.textContent = 'Remove Entire Bundle';
      footer.appendChild(button);
      footerCell.appendChild(footer);
      footerRow.appendChild(footerCell);
      entries[entries.length - 1].row.after(footerRow);
    });
  }

  // ─── Capture phase + isCartPage guard ───
  document.addEventListener('click', async event => {
    if (!isCartPage()) return;

    // Remove Entire Bundle
    const bundleButton = event.target.closest('.cart-remove-bundle');
    if (bundleButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isRemoving) return;
      const bundleId = bundleButton.dataset.bundleId;
      if (!bundleId) { alert('The bundle ID is missing.'); return; }
      isRemoving = true;
      bundleButton.disabled = true;
      bundleButton.textContent = 'Removing…';
      try {
        await removeBundle(bundleId);
        window.location.reload();
      } catch (error) {
        console.error('[Cart page] Bundle removal failed:', error);
        alert(error.message || 'Unable to remove the bundle.');
        bundleButton.disabled = false;
        bundleButton.textContent = 'Remove Entire Bundle';
      } finally {
        isRemoving = false;
      }
      return;
    }

    // ─── FIX: Individual bundle item Remove ───
    const individualButton = event.target.closest('.bundle-item-remove');
    if (individualButton) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isRemoving) return;
      const lineKey = individualButton.dataset.lineKey;
      if (!lineKey) { alert('Line-item key is missing.'); return; }
      isRemoving = true;
      individualButton.disabled = true;
      individualButton.textContent = 'Removing…';
      try {
        await removeBundleItem(lineKey);
        window.location.reload();
      } catch (error) {
        console.error('[Cart page] Bundle item removal failed:', error);
        alert(error.message || 'Unable to remove this item.');
        individualButton.disabled = false;
        individualButton.textContent = 'Remove';
      } finally {
        isRemoving = false;
      }
      return;
    }

    // Regular (non-bundle) item remove
    const removeLink =
      event.target.closest('line-item-quantity a') ||
      event.target.closest('a[href*="cart/change"]');
    if (!removeLink) return;
    const lineItem = removeLink.closest('line-item[data-line-key]');
    if (!lineItem || lineItem.dataset.bundleId?.trim()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (isRemoving) return;
    const lineKey = lineItem.dataset.lineKey;
    if (!lineKey) { alert('Line-item key is missing.'); return; }
    isRemoving = true;
    try {
      await removeRegularItem(lineKey);
      window.location.reload();
    } catch (error) {
      console.error('[Cart page] Regular item removal failed:', error);
      alert(error.message || 'Unable to remove this item.');
    } finally {
      isRemoving = false;
    }
  }, true); // ← capture phase

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBundleCartPage);
  } else {
    initBundleCartPage();
  }
})();